import React, { useState } from 'react';
import { X, Copy, Check, RefreshCw, Sparkles, Download, Code, Clock } from 'lucide-react';
import { ToolItem, ThemeMode } from '../types';
import { getToolVisualTheme } from '../data/toolVisuals';

interface QuickToolModalProps {
  tool: ToolItem | null;
  onClose: () => void;
  theme: ThemeMode;
}

export const QuickToolModal: React.FC<QuickToolModalProps> = ({
  tool,
  onClose,
  theme
}) => {
  if (!tool) return null;

  const isDark = theme === 'dark';
  const [copied, setCopied] = useState(false);

  const visual = getToolVisualTheme({ id: tool.id, name: tool.name });
  const IconComp = visual.icon;

  // Password generator state
  const [passLength, setPassLength] = useState(16);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [password, setPassword] = useState(() => generatePass(16, true, true));

  // Word counter state
  const [textInput, setTextInput] = useState("Dualis Tools provides over 450+ free online web tools. All calculations and transformations run instantly in your browser!");

  // Color picker state
  const [colorHex, setColorHex] = useState('#8b5cf6');

  // QR code state
  const [qrUrl, setQrUrl] = useState('https://dualistools.com');

  // JSON formatter state
  const [jsonInput, setJsonInput] = useState('{"name":"Dualis Tools","totalTools":450,"isFree":true}');
  const [jsonOutput, setJsonOutput] = useState('');
  const [jsonError, setJsonError] = useState('');

  function generatePass(len: number, sym: boolean, num: boolean) {
    let chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (num) chars += '0123456789';
    if (sym) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    let res = '';
    for (let i = 0; i < len; i++) {
      res += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return res;
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const wordCount = textInput.trim() ? textInput.trim().split(/\s+/).length : 0;
  const charCount = textInput.length;
  const sentenceCount = textInput.trim() ? textInput.split(/[.!?]+/).filter(Boolean).length : 0;

  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setJsonOutput(JSON.stringify(parsed, null, 2));
      setJsonError('');
    } catch (err: any) {
      setJsonError(err.message || 'Invalid JSON syntax');
      setJsonOutput('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className={`relative w-full max-w-2xl max-h-[90vh] rounded-2xl border flex flex-col shadow-2xl overflow-hidden ${
          isDark
            ? 'bg-[#0f121e] border-slate-800 text-white shadow-purple-950/40'
            : 'bg-white border-slate-200 text-slate-900 shadow-slate-300'
        }`}
      >
        {/* Header */}
        <div className={`p-5 border-b flex items-center justify-between ${
          isDark ? 'border-slate-800 bg-[#121626]' : 'border-slate-100 bg-slate-50'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${visual.bgClass} flex items-center justify-center shrink-0`}>
              <IconComp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">{tool.name}</h3>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {tool.description}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className={`p-2 rounded-xl transition-colors ${
              isDark ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body depending on tool type */}
        <div className="p-6 overflow-y-auto space-y-6">
          
          {/* PASSWORD GENERATOR */}
          {(tool.actionType === 'password' || tool.id.includes('password')) && (
            <div className="space-y-5">
              <div className={`p-4 rounded-xl border flex items-center justify-between font-mono text-lg font-bold tracking-wider break-all ${
                isDark ? 'bg-slate-950 border-slate-800 text-purple-300' : 'bg-slate-100 border-slate-200 text-purple-700'
              }`}>
                <span>{password}</span>
                <div className="flex items-center gap-2 ml-2">
                  <button
                    onClick={() => setPassword(generatePass(passLength, includeSymbols, includeNumbers))}
                    className="p-2 rounded-lg hover:bg-purple-500/10 text-purple-400"
                    title="Regenerate"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => copyToClipboard(password)}
                    className="p-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <span>Password Length:</span>
                    <span>{passLength} characters</span>
                  </div>
                  <input
                    type="range"
                    min={8}
                    max={64}
                    value={passLength}
                    onChange={(e) => {
                      const l = parseInt(e.target.value);
                      setPassLength(l);
                      setPassword(generatePass(l, includeSymbols, includeNumbers));
                    }}
                    className="w-full accent-purple-500 cursor-pointer"
                  />
                </div>

                <div className="flex flex-wrap gap-4 text-xs font-semibold">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeSymbols}
                      onChange={(e) => {
                        setIncludeSymbols(e.target.checked);
                        setPassword(generatePass(passLength, e.target.checked, includeNumbers));
                      }}
                      className="rounded accent-purple-500"
                    />
                    Include Symbols (!@#$)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeNumbers}
                      onChange={(e) => {
                        setIncludeNumbers(e.target.checked);
                        setPassword(generatePass(passLength, includeSymbols, e.target.checked));
                      }}
                      className="rounded accent-purple-500"
                    />
                    Include Numbers (0-9)
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* WORD COUNTER */}
          {(tool.actionType === 'wordcount' || tool.id.includes('word')) && (
            <div className="space-y-4">
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Type or paste your text here..."
                rows={5}
                className={`w-full p-4 rounded-xl border outline-none text-sm transition-colors ${
                  isDark
                    ? 'bg-slate-950 border-slate-800 text-white placeholder-slate-600 focus:border-purple-500'
                    : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                }`}
              />

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-100 border-slate-200'}`}>
                  <div className="text-xl font-bold text-purple-500">{wordCount}</div>
                  <div className="text-[11px] text-slate-400 font-medium">Words</div>
                </div>
                <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-100 border-slate-200'}`}>
                  <div className="text-xl font-bold text-indigo-500">{charCount}</div>
                  <div className="text-[11px] text-slate-400 font-medium">Characters</div>
                </div>
                <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-100 border-slate-200'}`}>
                  <div className="text-xl font-bold text-blue-500">{sentenceCount}</div>
                  <div className="text-[11px] text-slate-400 font-medium">Sentences</div>
                </div>
              </div>
            </div>
          )}

          {/* COLOR PICKER */}
          {(tool.actionType === 'color' || tool.id.includes('color')) && (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={colorHex}
                  onChange={(e) => setColorHex(e.target.value)}
                  className="w-16 h-16 rounded-xl border-2 border-slate-700 cursor-pointer bg-transparent"
                />
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Selected Color</div>
                  <div className="text-2xl font-mono font-extrabold">{colorHex.toUpperCase()}</div>
                </div>
              </div>

              <div className={`p-4 rounded-xl border flex items-center justify-between ${
                isDark ? 'bg-slate-950 border-slate-800' : 'bg-slate-100 border-slate-200'
              }`}>
                <span className="font-mono text-sm">HEX: {colorHex.toUpperCase()}</span>
                <button
                  onClick={() => copyToClipboard(colorHex)}
                  className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 flex items-center gap-1.5"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Copied' : 'Copy HEX'}</span>
                </button>
              </div>
            </div>
          )}

          {/* QR CODE GENERATOR */}
          {(tool.actionType === 'qr' || tool.id.includes('qr')) && (
            <div className="space-y-5 text-center">
              <div>
                <label className="block text-xs font-bold text-left mb-1 text-slate-400">URL or Text to encode:</label>
                <input
                  type="text"
                  value={qrUrl}
                  onChange={(e) => setQrUrl(e.target.value)}
                  className={`w-full p-3 rounded-xl border outline-none text-sm ${
                    isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'
                  }`}
                />
              </div>

              <div className="flex justify-center my-4">
                <div className="p-4 bg-white rounded-2xl shadow-xl border border-slate-200 inline-block">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrUrl || 'https://dualistools.com')}`}
                    alt="Generated QR Code"
                    className="w-44 h-44"
                  />
                </div>
              </div>
            </div>
          )}

          {/* JSON FORMATTER */}
          {(tool.actionType === 'json' || tool.id.includes('json')) && (
            <div className="space-y-4">
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder="Paste unformatted JSON here..."
                rows={4}
                className={`w-full p-3 font-mono text-xs rounded-xl border outline-none ${
                  isDark ? 'bg-slate-950 border-slate-800 text-purple-300' : 'bg-slate-50 border-slate-200 text-purple-800'
                }`}
              />

              <button
                onClick={formatJson}
                className="w-full py-2.5 rounded-xl bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition-colors"
              >
                Format & Validate JSON
              </button>

              {jsonError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl">
                  {jsonError}
                </div>
              )}

              {jsonOutput && (
                <pre className={`p-3 rounded-xl border font-mono text-xs overflow-x-auto max-h-40 ${
                  isDark ? 'bg-slate-950 border-slate-800 text-emerald-400' : 'bg-slate-100 border-slate-200 text-emerald-700'
                }`}>
                  {jsonOutput}
                </pre>
              )}
            </div>
          )}

          {/* GENERIC TOOL VIEW FOR OTHER TOOLS */}
          {!['password', 'wordcount', 'color', 'qr', 'json'].includes(tool.actionType || '') && 
           !tool.id.includes('password') && !tool.id.includes('word') && !tool.id.includes('color') && !tool.id.includes('qr') && !tool.id.includes('json') && (
            <div className="text-center py-10 space-y-5">
              <div className="inline-flex items-center justify-center p-1.5 rounded-3xl bg-slate-100 dark:bg-slate-800/50 shadow-inner">
                <div className={`w-16 h-16 rounded-2xl ${visual.bgClass} flex items-center justify-center shadow-lg`}>
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
              <div>
                <h4 className="text-xl sm:text-2xl font-black tracking-tight mb-2">Coming Soon!</h4>
                <p className={`text-sm max-w-sm mx-auto font-medium leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Our team is actively building <span className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{tool.name}</span>. It will be available in an upcoming Dualis Tools update!
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className={`p-4 border-t text-center text-xs ${
          isDark ? 'border-slate-800 text-slate-500 bg-[#0d101a]' : 'border-slate-100 text-slate-500 bg-slate-50'
        }`}>
          Dualis Tools • 100% Client-Side Processing • Free & Unlimited
        </div>
      </div>
    </div>
  );
};
